import { ethers } from "ethers";
import useSWR from "swr";

export const ONDO_HEDGER_ADDRESS = "0xe3123d309d5b26847816bc100c54bd9f608c6a23";

const MAINNET_RPC_URL =
  process.env.REACT_APP_ETHEREUM_RPC_URL ||
  "https://ethereum-rpc.publicnode.com";

const ERC20_ABI = ["function balanceOf(address) view returns (uint256)"];

const ONDO_HEDGER_TOKENS = [
  {
    symbol: "ETH",
    decimals: 18,
  },
  {
    symbol: "NVDAon",
    address: "0x2D1F7226Bd1F780AF6B9A49DCC0aE00E8Df4bDEE",
    decimals: 18,
  },
  {
    symbol: "TSLAon",
    address: "0xf6b1117ec07684D3958caD8BEb1b302bfD21103f",
    decimals: 18,
  },
  {
    symbol: "SPYon",
    address: "0xFeDC5f4a6c38211c1338aa411018DFAf26612c08",
    decimals: 18,
  },
  {
    symbol: "QQQon",
    address: "0x0e397938C1Aa0680954093495B70A9F5e2249aBa",
    decimals: 18,
  },
  {
    symbol: "HOODon",
    address: "0x998f02A9E343EF6E3E6f28700d5A20F839fD74E6",
    decimals: 18,
  },
  {
    symbol: "GOOGLon",
    address: "0xbA47214eDd2bb43099611b208f75E4b42FDcfEDc",
    decimals: 18,
  },
  {
    symbol: "USDC",
    address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    decimals: 6,
  },
];

export interface IOndoHedgerBalance {
  symbol: string;
  token_address?: string;
  raw_balance: string;
  balance: string;
  decimals: number;
}

export interface IOndoHedgerBalances {
  address: string;
  balances: IOndoHedgerBalance[];
  rpc_url: string;
}

const formatBalance = (
  rawBalance: ethers.BigNumber,
  decimals: number,
  precision = 6
) => {
  const formatted = ethers.utils.formatUnits(rawBalance, decimals);
  const [integer, fraction = ""] = formatted.split(".");
  const trimmedFraction = fraction.slice(0, precision).replace(/0+$/, "");

  return trimmedFraction ? `${integer}.${trimmedFraction}` : integer;
};

const fetchOndoHedgerBalances = async (): Promise<IOndoHedgerBalances> => {
  const provider = new ethers.providers.StaticJsonRpcProvider(
    MAINNET_RPC_URL,
    1
  );

  const balances = await Promise.all(
    ONDO_HEDGER_TOKENS.map(async (token) => {
      if (!token.address) {
        const rawBalance = await provider.getBalance(ONDO_HEDGER_ADDRESS);
        return {
          symbol: token.symbol,
          raw_balance: rawBalance.toString(),
          balance: formatBalance(rawBalance, token.decimals),
          decimals: token.decimals,
        };
      }

      const contract = new ethers.Contract(token.address, ERC20_ABI, provider);
      const rawBalance = (await contract.balanceOf(
        ONDO_HEDGER_ADDRESS
      )) as ethers.BigNumber;
      return {
        symbol: token.symbol,
        token_address: token.address,
        raw_balance: rawBalance.toString(),
        balance: formatBalance(rawBalance, token.decimals),
        decimals: token.decimals,
      };
    })
  );

  return {
    address: ONDO_HEDGER_ADDRESS,
    balances,
    rpc_url: MAINNET_RPC_URL,
  };
};

export const useOndoHedgerBalances = () => {
  const swr = useSWR<IOndoHedgerBalances, Error>(
    ["ondo-hedger-balances", ONDO_HEDGER_ADDRESS, MAINNET_RPC_URL],
    fetchOndoHedgerBalances,
    { refreshInterval: 30000 }
  );

  return {
    balances: swr.data?.balances ?? [],
    data: swr.data,
    error: swr.error,
    isLoading: swr.isLoading,
    isValidating: swr.isValidating,
    refresh: swr.mutate,
  };
};
